import { CaretLeft, CaretRight, DotsSixVertical } from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import * as PanelPrimitive from "react-resizable-panels";

export const PanelGroup = PanelPrimitive.PanelGroup;

export const Panel = PanelPrimitive.Panel;

export type { ImperativePanelHandle } from "react-resizable-panels";

const RAIL_WIDTH_PX = 24;

type PanelResizeHandleProps = React.ComponentProps<typeof PanelPrimitive.PanelResizeHandle> & {
  isDragging?: boolean;
  onDoubleClick?: () => void;
  /** When true, handle acts as a rail: shows railIcon, click calls onExpand. No drag. */
  collapsed?: boolean;
  onExpand?: () => void;
  /** When provided and not collapsed, shows a collapse chevron on the strip. */
  onCollapse?: () => void;
  /** Which side the adjacent panel is on; chevron is placed on that edge. */
  collapseSide?: "left" | "right";
  /** Accessible label for the collapse button (e.g. "Hide editor"). */
  collapseLabel?: string;
  railIcon?: React.ReactNode;
  railLabel?: string;
};

export const PanelResizeHandle = ({
  className,
  isDragging,
  onDragging,
  onDoubleClick,
  collapsed = false,
  onExpand,
  onCollapse,
  collapseSide,
  collapseLabel,
  railIcon,
  railLabel,
  ...props
}: PanelResizeHandleProps) => (
  <PanelPrimitive.PanelResizeHandle
    className={cn(
      "relative z-[70] flex h-full shrink-0 items-stretch",
      collapsed && "!min-w-[24px]",
      className,
    )}
    onDragging={onDragging}
    {...props}
  >
    {collapsed && onExpand ? (
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          onExpand();
        }}
        className={cn(
          "flex h-full min-w-6 flex-shrink-0 cursor-pointer items-center justify-center",
          "border-border/50 bg-background/90 backdrop-blur-sm",
          "transition-colors hover:bg-secondary/60 hover:border-primary/20",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          collapseSide === "left" ? "border-r rounded-r-sm" : "border-l rounded-l-sm",
        )}
        style={{ width: RAIL_WIDTH_PX }}
        title={railLabel}
        aria-label={railLabel ?? "Expand panel"}
      >
        <span className="text-foreground/70 transition-colors hover:text-primary">
          {railIcon}
        </span>
      </button>
    ) : (
      <div
        className={cn(
          "relative flex h-full w-4 cursor-col-resize items-center justify-center transition-colors hover:bg-info/10 active:bg-info/15",
          isDragging && "bg-info/15",
        )}
        onDoubleClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDoubleClick?.();
        }}
        title={onDoubleClick ? "Double-click to reset width" : undefined}
      >
        <div
          className={cn(
            "h-12 w-1 rounded-full bg-border transition-all hover:bg-info hover:w-1.5",
            isDragging && "bg-info w-1.5",
          )}
        />
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <DotsSixVertical size={14} className="text-muted-foreground/80" />
        </div>
        {onCollapse && collapseSide && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onCollapse();
            }}
            className={cn(
              "absolute top-1/2 z-10 flex h-6 w-5 -translate-y-1/2 cursor-pointer items-center justify-center",
              "text-muted-foreground/80 transition-colors hover:bg-info/15 hover:text-foreground",
              "focus:outline-none focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-inset focus-visible:rounded",
              collapseSide === "left" ? "left-0" : "right-0",
            )}
            title={collapseLabel}
            aria-label={collapseLabel ?? "Collapse panel"}
          >
            {collapseSide === "left" ? (
              <CaretLeft size={10} weight="bold" />
            ) : (
              <CaretRight size={10} weight="bold" />
            )}
          </button>
        )}
      </div>
    )}
  </PanelPrimitive.PanelResizeHandle>
);
